import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { LIVE_TRANSLATION_LANGUAGES } from "@taskhealth/shared";
import { getRecordingPermissionsAsync, requestRecordingPermissionsAsync } from "expo-audio";
import { useEffect, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";

import type { useLiveTranslationSession } from "@/lib/live-translation/useLiveTranslationSession";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

type SessionHandle = ReturnType<typeof useLiveTranslationSession>;

interface Props {
    visible: boolean;
    visitId: string | null;
    preselectedLanguage: string | null;
    session: SessionHandle;
    onClose: () => void;
}

type Permission = "checking" | "granted" | "needs-prompt" | "denied-permanent";

export function LiveTranslationModal({
    visible,
    visitId,
    preselectedLanguage,
    session,
    onClose
}: Props) {
    const [permission, setPermission] = useState<Permission>("checking");
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(preselectedLanguage);

    // Probe the current OS permission silently every time the modal opens.
    // If the nurse already granted mic access on a prior session, we skip
    // straight to the language picker — no "Microphone Access Required" screen.
    useEffect(() => {
        if (!visible) {
            return;
        }
        setSelectedLanguage(preselectedLanguage);
        let cancelled = false;
        (async () => {
            const status = await getRecordingPermissionsAsync();
            if (cancelled) {
                return;
            }
            if (status.granted) {
                setPermission("granted");
            } else if (status.canAskAgain) {
                setPermission("needs-prompt");
            } else {
                setPermission("denied-permanent");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, preselectedLanguage]);

    async function handleRequestPermission() {
        const status = await requestRecordingPermissionsAsync();
        if (status.granted) {
            setPermission("granted");
        } else if (status.canAskAgain) {
            setPermission("needs-prompt");
        } else {
            setPermission("denied-permanent");
        }
    }

    async function handleStart() {
        if (!visitId || !selectedLanguage) {
            return;
        }
        await session.start({ visitId, targetLanguage: selectedLanguage });
        // Provider observes session.status and closes the modal once
        // connecting; the call above resolves once setup is kicked off so
        // there's nothing more to do here.
    }

    function handleRequestClose() {
        if (session.status === "error" || session.status === "idle") {
            session.reset();
        }
        onClose();
    }

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={handleRequestClose}
        >
            <View style={styles.backdrop}>
                <View style={styles.card}>
                    <View style={styles.header}>
                        <Text style={styles.title}>Live Translation</Text>
                        <Pressable
                            onPress={handleRequestClose}
                            hitSlop={12}
                            accessibilityLabel="Close"
                        >
                            <MaterialCommunityIcons
                                name="close"
                                size={22}
                                color={colors.textSecondary}
                            />
                        </Pressable>
                    </View>

                    {renderBody({
                        session,
                        permission,
                        selectedLanguage,
                        setSelectedLanguage,
                        onRequestPermission: handleRequestPermission,
                        onStart: handleStart
                    })}
                </View>
            </View>
        </Modal>
    );
}

function renderBody(args: {
    session: SessionHandle;
    permission: Permission;
    selectedLanguage: string | null;
    setSelectedLanguage: (code: string) => void;
    onRequestPermission: () => Promise<void>;
    onStart: () => Promise<void>;
}) {
    const {
        session,
        permission,
        selectedLanguage,
        setSelectedLanguage,
        onRequestPermission,
        onStart
    } = args;

    if (session.status === "error") {
        return (
            <View style={styles.body}>
                <MaterialCommunityIcons
                    name="alert-circle-outline"
                    size={36}
                    color={colors.error500}
                />
                <Text style={styles.errorTitle}>Translation couldn&apos;t start</Text>
                <Text style={styles.errorMessage}>{friendlyError(session.errorMessage)}</Text>
                <Pressable style={styles.primaryButton} onPress={() => session.reset()}>
                    <Text style={styles.primaryButtonText}>Try again</Text>
                </Pressable>
            </View>
        );
    }

    if (session.status === "connecting") {
        return (
            <View style={styles.body}>
                <Text style={styles.subtitle}>Connecting…</Text>
            </View>
        );
    }

    if (permission === "checking") {
        return (
            <View style={styles.body}>
                <Text style={styles.helperText}>Checking microphone access…</Text>
            </View>
        );
    }

    if (permission === "denied-permanent") {
        return (
            <View style={styles.body}>
                <MaterialCommunityIcons name="microphone-off" size={36} color={colors.error500} />
                <Text style={styles.errorTitle}>Microphone access blocked</Text>
                <Text style={styles.errorMessage}>
                    Enable microphone access in Settings to use Live Translation.
                </Text>
            </View>
        );
    }

    if (permission === "needs-prompt") {
        return (
            <View style={styles.body}>
                <MaterialCommunityIcons name="microphone" size={36} color={colors.info600} />
                <Text style={styles.subtitle}>Microphone Access Required</Text>
                <Text style={styles.helperText}>
                    Live Translation needs access to your microphone to translate the conversation
                    in real time.
                </Text>
                <Pressable style={styles.primaryButton} onPress={onRequestPermission}>
                    <Text style={styles.primaryButtonText}>Allow Microphone Access</Text>
                </Pressable>
            </View>
        );
    }

    // permission === "granted" → language picker
    return (
        <View style={styles.body}>
            <Text style={styles.subtitle}>Target Language</Text>
            <Text style={styles.helperText}>
                We&apos;ll translate between English and the language you pick. The patient&apos;s
                spoken language is detected automatically.
            </Text>
            <View style={styles.languageGrid}>
                {LIVE_TRANSLATION_LANGUAGES.map((lang) => {
                    const selected = lang.code === selectedLanguage;
                    return (
                        <Pressable
                            key={lang.code}
                            style={[styles.languagePill, selected && styles.languagePillSelected]}
                            onPress={() => setSelectedLanguage(lang.code)}
                        >
                            <Text
                                style={[
                                    styles.languagePillText,
                                    selected && styles.languagePillTextSelected
                                ]}
                            >
                                {lang.label}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>
            <Pressable
                style={[styles.primaryButton, !selectedLanguage && styles.primaryButtonDisabled]}
                onPress={onStart}
                disabled={!selectedLanguage}
            >
                <Text style={styles.primaryButtonText}>Start Translation</Text>
            </Pressable>
        </View>
    );
}

// Translate raw upstream / network errors into something readable. We don't
// dump the OpenAI JSON envelope onto a clinical screen.
function friendlyError(raw: string | null): string {
    if (!raw) {
        return "Something went wrong. Please try again.";
    }
    if (/OPENAI_API_KEY|not configured/i.test(raw)) {
        return "Translation service isn't configured. Contact support.";
    }
    // Specific business errors first — otherwise they fell through to the vague
    // "Something went wrong", which is what made unrelated failures look
    // identical in the field.
    if (/limit reached|daily .*limit/i.test(raw)) {
        return "You've reached today's translation limit. It resets in 24 hours.";
    }
    if (/clock(ed)? in/i.test(raw)) {
        return "Clock in to the visit before starting translation.";
    }
    if (/network|timeout|timed out|fetch|abort|connect to live translation/i.test(raw)) {
        return "Network was unstable. Try again with a stronger connection.";
    }
    if (/OpenAI|Gemini|provider|session/i.test(raw)) {
        return "The translation service rejected the request. Please try again.";
    }
    return "Something went wrong. Please try again.";
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(15, 10, 40, 0.45)",
        justifyContent: "center",
        alignItems: "center",
        padding: spacing.lg
    },
    card: {
        width: "100%",
        maxWidth: 480,
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        padding: spacing.lg,
        ...shadows.lg
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: spacing.base
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary
    },
    body: {
        gap: spacing.base,
        alignItems: "stretch"
    },
    subtitle: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.textPrimary
    },
    helperText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 19
    },
    errorTitle: {
        fontFamily: fonts.bold,
        fontSize: 16,
        color: colors.textPrimary,
        textAlign: "center"
    },
    errorMessage: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        textAlign: "center"
    },
    languageGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm
    },
    languagePill: {
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.borderStrong,
        backgroundColor: colors.surface
    },
    languagePillSelected: {
        backgroundColor: colors.info50,
        borderColor: colors.info500
    },
    languagePillText: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textSecondary
    },
    languagePillTextSelected: {
        color: colors.info600,
        fontFamily: fonts.semiBold
    },
    primaryButton: {
        flexDirection: "row",
        gap: spacing.sm,
        backgroundColor: colors.info500,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        borderRadius: radius.md,
        alignItems: "center",
        justifyContent: "center"
    },
    primaryButtonDisabled: {
        opacity: 0.4
    },
    primaryButtonText: {
        color: colors.surface,
        fontFamily: fonts.bold,
        fontSize: 14
    }
});
